import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { Button, CopyToClipboardButton, EmptyState, FlexBox, Icon, IconButton, Text, Tooltip, } from '@dixa/lib-shared-habla';
import { colors } from '@dixa/lib-shared-tokens';
import { useCallback, useState } from 'react';
import { useAnalyticsState } from 'analytics/components/panels/utils/useAnalyticsState/useAnalyticsState';
import { appActions } from 'app/app.actions';
import { analytics } from 'app/utils/segment';
import { SafeInnerHtmlSetter } from 'components/shared/util/SafeInnerHtmlSetter';
import { markdownToHtml } from 'utils/markdown';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { isInsightInstrument } from '../../../../utils/groupInstruments';
import styles from './instrumentInsightsInsight.module.scss';
export const InstrumentInsightsInsight = ({ instruments, panel, prompt, requestId, onRefetch, }) => {
    var _a;
    const dispatch = useAppDispatch();
    const { dashboard } = useAnalyticsState();
    const [feedback, setFeedback] = useState(null);
    const handleRefetch = useCallback(() => {
        onRefetch();
        setFeedback(null);
        analytics.analyticsInsightsRegenerateClicked({
            dashboard: dashboard.title,
            panel: panel.title,
            prompt: prompt.title,
        });
    }, [dashboard.title, onRefetch, panel.title, prompt.title]);
    const handleCopyToClipboard = useCallback(() => {
        analytics.analyticsInsightsCopyClicked({
            dashboard: dashboard.title,
            panel: panel.title,
            prompt: prompt.title,
        });
    }, [dashboard.title, panel.title, prompt.title]);
    const handleFeedback = useCallback((feedback) => {
        setFeedback(feedback);
        analytics.analyticsInsightsFeedbackClicked({
            dashboard: dashboard.title,
            panel: panel.title,
            prompt: prompt.title,
            feedback,
            requestId,
        });
    }, [dashboard.title, panel.title, prompt.title, requestId]);
    const handleAdditionalFeedback = useCallback(() => {
        if (!feedback)
            return;
        dispatch(appActions.showModal({
            modal: {
                type: 'app:feedbackForm',
                arg: {
                    title: 'Analytics AI Insights Feedback',
                    team: 'insights',
                    placeholder: feedback === 'useful'
                        ? 'What do you like about the summary?'
                        : 'What was the issue with the summary? How could it be improved?',
                    extra: {
                        panel: panel.title,
                        prompt: prompt.title,
                        feedback,
                        requestId,
                    },
                },
            },
        }));
    }, [dispatch, feedback, panel.title, prompt.title, requestId]);
    const handleThumbsUp = useCallback(() => {
        handleFeedback('useful');
    }, [handleFeedback]);
    const handleThumbsDown = useCallback(() => {
        handleFeedback('not_useful');
    }, [handleFeedback]);
    const insightInstrument = instruments.find(isInsightInstrument);
    if (!insightInstrument) {
        return (_jsx(EmptyState, { title: "No data available", description: "There is no data available to generate insights.", icon: "ai" }));
    }
    return (_jsxs("div", Object.assign({ className: styles.insight }, { children: [_jsxs(FlexBox, Object.assign({ alignItems: "center", gap: "300" }, { children: [_jsx(Icon, { name: (_a = prompt.icon) !== null && _a !== void 0 ? _a : 'ai', color: colors.purple[700], size: 24 }), _jsx(Text.Heading, Object.assign({ variant: "heading-3" }, { children: prompt.title })), _jsxs(FlexBox, Object.assign({ gap: "200", flex: "1", justifyContent: "flex-end", alignItems: "center" }, { children: [_jsx(Tooltip, Object.assign({ dark: true, content: "Regenerate insight" }, { children: _jsx(IconButton, { onClick: handleRefetch, variant: "tertiary", icon: "sync" }) })), _jsx(Tooltip, Object.assign({ dark: true, content: "Copy text to clipboard" }, { children: _jsx(CopyToClipboardButton, { textToCopy: insightInstrument.data, tooltip: false, clickedProps: { icon: 'done' }, buttonProps: { variant: 'tertiary', icon: 'copy' }, onClick: handleCopyToClipboard }) })), feedback ? (_jsx(Button, Object.assign({ onClick: handleAdditionalFeedback, variant: "tertiary", icon: feedback === 'useful' ? 'thumbs-up' : 'thumbs-down' }, { children: "Tell us more" }))) : (_jsxs(_Fragment, { children: [_jsx(Tooltip, Object.assign({ dark: true, content: "This insight is not helpful" }, { children: _jsx(IconButton, { onClick: handleThumbsDown, variant: "tertiary", icon: "thumbs-down" }) })), _jsx(Tooltip, Object.assign({ dark: true, content: "This insight is helpful" }, { children: _jsx(IconButton, { onClick: handleThumbsUp, variant: "tertiary", icon: "thumbs-up" }) }))] }))] }))] })), _jsx(Text.Paragraph, Object.assign({ variant: "2", subdued: true }, { children: _jsx(SafeInnerHtmlSetter, { element: "div", content: markdownToHtml(insightInstrument.data) }) }))] })));
};
